<template>
    <div class="absence-report" dir="rtl">
        <Breadcrumb :items="breadcrumbs" />
        <div class="container-fluid my-3">
        <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
            <h4 class="mb-0">تقرير الغياب <span v-if="grade" class="text-muted">— {{ grade.name }}</span></h4>
            <router-link :to="{ name: 'gradeAttendance', params: { gradeSlug } }" class="btn btn-outline-secondary">
                <i class="pi pi-arrow-right me-1" aria-hidden="true"></i> العودة للحضور
            </router-link>
        </div>

        <div class="card mb-3">
            <div class="card-body p-3">
                <div class="row g-3 align-items-end">
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-mode" class="form-label">نوع التقرير</label>
                        <Dropdown inputId="report-mode" v-model="mode" :options="modes" optionLabel="name"
                            optionValue="id" class="w-100" @change="fetchReport" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <template v-if="mode === 'month'">
                            <label for="report-month" class="form-label">الشهر</label>
                            <input id="report-month" type="month" class="form-control" v-model="month"
                                :max="today.slice(0, 7)" @change="fetchReport" />
                        </template>
                        <template v-else>
                            <label for="report-date" class="form-label">التاريخ</label>
                            <input id="report-date" type="date" class="form-control" v-model="date"
                                :max="today" @change="fetchReport" />
                        </template>
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-search" class="form-label">اسم الطالب أو رقم الحجز</label>
                        <input id="report-search" type="search" class="form-control" v-model="word"
                            placeholder="ابحث بالاسم أو رقم الحجز" @input="debounceFetch" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-gender" class="form-label">الجنس</label>
                        <Dropdown inputId="report-gender" v-model="gender" :options="genders" optionLabel="title"
                            optionValue="id" showClear placeholder="الكل" class="w-100" @change="fetchReport" />
                    </div>

                </div>
                <p class="text-muted small mt-3 mb-0">يُحسب الغياب من أيام الشهر التي لم يُسجل فيها حضور، حتى اليوم فقط، وفق نظام الحضور الحالي.</p>
            </div>
        </div>

        <div class="d-flex gap-2 flex-wrap mb-3">
            <button type="button" class="btn btn-success" :disabled="!canExport" @click="exportToExcel">
                <i class="pi pi-file-excel me-1" aria-hidden="true"></i> تصدير Excel
            </button>
            <button type="button" class="btn btn-danger" :disabled="!canExport" v-print="'#absenceReportTable'">
                <i class="pi pi-print me-1" aria-hidden="true"></i> طباعة / PDF
            </button>
        </div>

        <Loading v-if="loading" />
        <div v-else-if="error" class="alert alert-danger" role="alert">
            {{ error }}
            <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="initialize">إعادة المحاولة</button>
        </div>
        <div v-else-if="report" id="absenceReportTable" dir="rtl">
            <div class="card mb-3">
                <div class="card-body p-3">
                    <h5>{{ periodTitle }} <bdi>{{ report.period }}</bdi></h5>
                    <p class="mb-1">{{ report.academic_year.name }}</p>
                    <p class="mb-1" v-if="report.through_date">الاحتساب حتى: <bdi>{{ report.through_date }}</bdi></p>
                    <div class="d-flex gap-3 flex-wrap">
                        <span>عدد الطلاب: <strong>{{ report.students.length }}</strong></span>
                        <span>طلاب لديهم غياب: <strong>{{ report.students_with_absences }}</strong></span>
                        <span>إجمالي أيام الغياب: <strong>{{ report.total_absence_days }}</strong></span>
                    </div>
                    <p v-if="report.students.length && !report.total_absence_days" class="text-success mt-2 mb-0" role="status">
                        لا توجد حالات غياب للفترة المحددة.
                    </p>
                </div>
            </div>
            <div class="table-responsive">
                <table class="table table-bordered align-middle">
                    <thead>
                        <tr>
                            <th scope="col">اسم الطالب</th>
                            <th scope="col">رقم الحجز</th>
                            <th scope="col">رقم ولي الأمر</th>
                            <th scope="col" class="text-center">عدد أيام الغياب</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="student in report.students" :key="student.user_id">
                            <td>{{ student.name }}</td>
                            <td><bdi>{{ student.reservation_number }}</bdi></td>
                            <td><bdi>{{ student.parent_phone || '—' }}</bdi></td>
                            <td class="text-center fw-bold" :class="student.absence_days ? 'text-danger' : 'text-success'">
                                {{ student.absence_days }}
                            </td>
                        </tr>
                        <tr v-if="!report.students.length">
                            <td colspan="4" class="text-center text-muted py-5" role="status">
                                {{ report.mode === 'day' ? 'لا يوجد طلاب غائبون في التاريخ المحدد وفق الفلاتر الحالية.' : 'لا يوجد طلاب مطابقون للفلاتر الحالية.' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import * as XLSX from 'xlsx/xlsx.mjs';

function cairoToday() {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const value = type => parts.find(part => part.type === type).value;
    return [value('year'), value('month'), value('day')].join('-');
}

export default {
    components: { Dropdown, Breadcrumb, Loading },
    data() {
        const today = cairoToday();
        const query = this.$route.query;
        return {
            today,
            mode: 'month',
            month: /^\d{4}-(0[1-9]|1[0-2])$/.test(query.month ?? '') ? query.month : today.slice(0, 7),
            date: today,
            word: typeof query.word === 'string' ? query.word : '',
            gender: ['1', '2'].includes(String(query.gender)) ? Number(query.gender) : null,
            modes: [{ id: 'month', name: 'ملخص الغياب الشهري' }, { id: 'day', name: 'غياب يوم محدد' }],
            genders: [{ id: 1, title: 'ولد' }, { id: 2, title: 'بنت' }],

            report: null,
            loading: true,
            error: '',
            searchTimeout: null,
            requestId: 0,
        };
    },
    computed: {
        gradeStore() { return useGradeStore(); },
        gradeSlug() { return this.$route.params.gradeSlug; },
        grade() {
            return this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
                || this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug);
        },
        breadcrumbs() {
            return [
                { name: 'الرئيسية', path: '/' },
                { name: this.grade?.name ?? this.gradeSlug, path: '/grades/' + this.gradeSlug + '/students' },
                { name: 'الحضور', path: '/grades/' + this.gradeSlug + '/attendance' },
                { name: 'تقرير الغياب', path: this.$route.fullPath },
            ];
        },
        periodTitle() {
            if (!this.report) return '';
            return this.report.mode === 'day' ? 'تقرير الغياب ليوم:' : 'ملخص الغياب لشهر:';
        },
        canExport() {
            return !this.loading && !this.error && !!this.report?.students.length;
        },
    },
    watch: {
        gradeSlug() {

            this.initialize();
        },
    },
    methods: {
        async initialize() {
            const initializationId = ++this.requestId;
            this.loading = true;
            this.error = '';
            this.report = null;
            try {
                if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
                if (initializationId !== this.requestId) return;
                if (!this.grade) throw new Error('grade');

                await this.fetchReport();
            } catch {
                if (initializationId !== this.requestId) return;
                this.error = 'تعذّر تحميل بيانات الفصل. يرجى إعادة المحاولة.';
                this.loading = false;
            }
        },
        debounceFetch() {
            clearTimeout(this.searchTimeout);
            // Hide the previous result and disable export as soon as filters change.
            this.requestId++;
            this.loading = true;
            this.searchTimeout = setTimeout(() => this.fetchReport(), 350);
        },
        async fetchReport() {
            clearTimeout(this.searchTimeout);
            const requestId = ++this.requestId;
            this.loading = true;
            this.error = '';
            this.report = null;
            if (!this.grade || !(this.mode === 'day' ? this.date : this.month)) {
                this.error = 'يرجى تحديد الفصل والفترة المطلوبة.';
                this.loading = false;
                return;
            }
            const params = {
                academic_year_id: this.grade.id,
                mode: this.mode,
                ...(this.mode === 'day' ? { date: this.date } : { month: this.month }),
            };
            if (this.word.trim()) params.word = this.word.trim();
            if (this.gender) params.gender = this.gender;

            try {
                const { data } = await axios.get('/attendance-absence-report', { params });
                if (requestId !== this.requestId) return;
                this.report = data.data;
            } catch {
                if (requestId !== this.requestId) return;
                this.error = 'تعذّر تحميل تقرير الغياب. يرجى التحقق من الفلاتر وإعادة المحاولة.';
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        exportToExcel() {
            if (!this.canExport) return;
            // Explicit string cells preserve leading zeroes in phone/booking numbers.
            const rows = this.report.students.map(student => [
                student.name, String(student.reservation_number), String(student.parent_phone ?? ''),
                this.report.period, student.absence_days,
            ]);
            const sheet = XLSX.utils.aoa_to_sheet([
                ['اسم الطالب', 'رقم الحجز', 'رقم ولي الأمر', this.report.mode === 'day' ? 'التاريخ' : 'الشهر', 'عدد أيام الغياب'],
                ...rows,
            ]);
            sheet['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 20 }, { wch: 16 }, { wch: 18 }];
            const book = XLSX.utils.book_new();
            book.Workbook = { Views: [{ RTL: true }] };
            XLSX.utils.book_append_sheet(book, sheet, 'تقرير الغياب');
            XLSX.writeFile(book, 'تقرير-الغياب-' + this.report.academic_year.name + '-' + this.report.period + '.xlsx');
        },
    },
    mounted() { this.initialize(); },
    beforeUnmount() {
        clearTimeout(this.searchTimeout);
        this.requestId++;
    },
};
</script>

<style scoped>
.absence-report .table {
    min-width: 540px;
}
#absenceReportTable .table tbody td {
    color: var(--table-cell-color);
    background-color: var(--table-cell-bg);
}
#absenceReportTable .table thead th {
    color: var(--table-cell-color) !important;
    background: var(--table-stripe-bg) !important;
}
.absence-report input[type="date"],
.absence-report input[type="month"] {
    direction: ltr;
    text-align: right;
}
@media print {
    #absenceReportTable {
        direction: rtl;
    }
    #absenceReportTable .card-body {
        color: var(--table-cell-color);
        background: var(--table-cell-bg);
    }
}
</style>
